import React, {useEffect, useState, useMemo} from 'react'
import {Line} from 'react-chartjs-2'
import 'chart.js/auto'
import {API_URL} from '../../../modules/auth/core/_requests'

interface CombinedChartData {
  date: string
  ecUnits: number
  ecRevenue: number
  vintageUnits: number
  vintageRevenue: number
  allUnits: number
  allRevenue: number
}

const dateOptions = [
  'last7Days',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'last30Days',
  'last90Days',
  'last6Months',
  'thisYear',
  'lastYear',
  'last12Months',
]

const dateLabels: {[key: string]: string} = {
  last7Days: 'Last 7 Days',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  last30Days: 'Last 30 Days',
  last90Days: 'Last 90 Days',
  last6Months: 'Last 6 Months',
  thisYear: 'This Year',
  lastYear: 'Last Year',
  last12Months: 'Last 12 Months',
}

const intervalOptions = ['day', 'week', 'month']

function parseIsoDate(str: string): Date {
  const parts = str.split('-').map(Number)
  if (parts.length === 3) {
    return new Date(parts[0], parts[1] - 1, parts[2])
  }
  return new Date(str)
}

function addInterval(base: Date, interval: string): Date {
  const d = new Date(base.getTime())
  if (interval === 'day') {
    d.setDate(d.getDate() + 1)
  } else if (interval === 'week') {
    d.setDate(d.getDate() + 7)
  } else {
    d.setMonth(d.getMonth() + 1)
  }
  return d
}

function formatDateYMD(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = ('0' + (d.getMonth() + 1)).slice(-2)
  const dd = ('0' + d.getDate()).slice(-2)
  return `${yyyy}-${mm}-${dd}`
}

// Added helper to format numbers with up to 2 decimal places
function formatNumber(num: number): string {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

function fillMissingDates(data: CombinedChartData[], interval: string): CombinedChartData[] {
  if (!data.length) return []
  const sorted = [...data].sort((a, b) => (a.date < b.date ? -1 : 1))
  const minDate = parseIsoDate(sorted[0].date)
  const maxDate = parseIsoDate(sorted[sorted.length - 1].date)
  const filled: CombinedChartData[] = []
  const map = new Map<string, CombinedChartData>()
  for (const row of data) {
    map.set(row.date, row)
  }
  let cursor = new Date(minDate.getTime())
  while (cursor.getTime() <= maxDate.getTime()) {
    const key = formatDateYMD(cursor)
    if (map.has(key)) {
      filled.push(map.get(key)!)
    } else {
      filled.push({
        date: key,
        ecUnits: 0,
        ecRevenue: 0,
        vintageUnits: 0,
        vintageRevenue: 0,
        allUnits: 0,
        allRevenue: 0,
      })
    }
    cursor = addInterval(cursor, interval)
  }
  return filled
}

async function fetchDataRange(date: string, interval: string): Promise<CombinedChartData[]> {
  const url = `${API_URL}/amazon-line-graph/chart-data?store=both&date=${encodeURIComponent(
    date
  )}&interval=${encodeURIComponent(interval)}`
  const resp = await fetch(url)
  const j = await resp.json()
  return j.data || []
}

async function fetchAll(interval: string): Promise<CombinedChartData[]> {
  const url = `${API_URL}/amazon-line-graph/chart-data?store=both&interval=${encodeURIComponent(
    interval
  )}`
  const resp = await fetch(url)
  const j = await resp.json()
  return j.data || []
}

function filterByDateRange(
  data: CombinedChartData[],
  interval: string,
  rangeKey: string
): CombinedChartData[] {
  if (!data.length) return []
  const now = new Date()
  let endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let startDate = new Date(endDate.getTime())

  if (rangeKey === 'last7Days') {
    startDate.setDate(startDate.getDate() - 6)
  } else if (rangeKey === 'thisWeek') {
    const dayOfWeek = startDate.getDay()
    startDate.setDate(startDate.getDate() - dayOfWeek)
  } else if (rangeKey === 'lastWeek') {
    const dayOfWeek = startDate.getDay()
    startDate.setDate(startDate.getDate() - dayOfWeek - 7)
    endDate = new Date(startDate.getTime())
    endDate.setDate(endDate.getDate() + 6)
  } else if (rangeKey === 'thisMonth') {
    startDate.setDate(1)
  } else if (rangeKey === 'lastMonth') {
    startDate.setMonth(startDate.getMonth() - 1, 1)
    endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    endDate.setMonth(endDate.getMonth() + 1, 0)
  } else if (rangeKey === 'last30Days') {
    startDate.setDate(startDate.getDate() - 29)
  } else if (rangeKey === 'last90Days') {
    startDate.setDate(startDate.getDate() - 89)
  } else if (rangeKey === 'last6Months') {
    startDate.setMonth(startDate.getMonth() - 5, 1)
  } else if (rangeKey === 'thisYear') {
    startDate = new Date(startDate.getFullYear(), 0, 1)
  } else if (rangeKey === 'lastYear') {
    startDate.setFullYear(startDate.getFullYear() - 1, 0, 1)
    endDate = new Date(startDate.getFullYear(), 11, 31)
  } else if (rangeKey === 'last12Months') {
    startDate.setFullYear(startDate.getFullYear(), startDate.getMonth() - 11, 1)
  }

  const minISO = formatDateYMD(startDate)
  const maxISO = formatDateYMD(endDate)
  const filtered = data.filter((d) => d.date >= minISO && d.date <= maxISO)
  const filled = fillMissingDates(filtered, interval)
  return filled
}

const cacheAll: {
  dayAll?: CombinedChartData[]
  weekAll?: CombinedChartData[]
  monthAll?: CombinedChartData[]
} = {}

export const AmazonSalesLineChart: React.FC = () => {
  const [loadingInitial, setLoadingInitial] = useState(true)
  const [loadingPrefetch, setLoadingPrefetch] = useState(false)
  const [initialData, setInitialData] = useState<CombinedChartData[]>([])
  const [activeTab, setActiveTab] = useState<'units' | 'revenue'>('revenue')
  const [selectedDate, setSelectedDate] = useState('last30Days')
  const [selectedInterval, setSelectedInterval] = useState('day')
  const [showCombined, setShowCombined] = useState(false)
  const [logScale, setLogScale] = useState(false)

  const [finalData, setFinalData] = useState<CombinedChartData[]>([])

  useEffect(() => {
    let cancel = false
    async function loadDefault() {
      const d = await fetchDataRange('last30Days', 'day')
      if (!cancel) {
        setInitialData(d)
        setLoadingInitial(false)
      }
    }
    loadDefault()

    return () => {
      cancel = true
    }
  }, [])

  useEffect(() => {
    if (!loadingInitial && initialData.length) {
      setFinalData(fillMissingDates(initialData, 'day'))
      setLoadingPrefetch(true)
      Promise.all([fetchAll('day'), fetchAll('week'), fetchAll('month')]).then(
        ([dayArr, weekArr, monthArr]) => {
          cacheAll.dayAll = dayArr
          cacheAll.weekAll = weekArr
          cacheAll.monthAll = monthArr
          setLoadingPrefetch(false)
        }
      )
    }
  }, [loadingInitial, initialData])

  useEffect(() => {
    if (selectedInterval === 'day') {
      if (cacheAll.dayAll) {
        const filtered = filterByDateRange(cacheAll.dayAll, 'day', selectedDate)
        setFinalData(filtered)
      } else {
        fetchDataRange(selectedDate, 'day').then((d) => {
          setFinalData(fillMissingDates(d, 'day'))
        })
      }
    } else if (selectedInterval === 'week') {
      if (cacheAll.weekAll) {
        const filtered = filterByDateRange(cacheAll.weekAll, 'week', selectedDate)
        setFinalData(filtered)
      } else {
        fetchDataRange(selectedDate, 'week').then((d) => {
          setFinalData(fillMissingDates(d, 'week'))
        })
      }
    } else {
      if (cacheAll.monthAll) {
        const filtered = filterByDateRange(cacheAll.monthAll, 'month', selectedDate)
        setFinalData(filtered)
      } else {
        fetchDataRange(selectedDate, 'month').then((d) => {
          setFinalData(fillMissingDates(d, 'month'))
        })
      }
    }
  }, [selectedDate, selectedInterval])

  const unitsData = useMemo(() => {
    return {
      labels: finalData.map((item) => item.date),
      datasets: [
        {
          label: 'EC AMZ Units',
          data: finalData.map((item) => item.ecUnits),
          borderColor: '#36A2EB',
          backgroundColor: 'rgba(54,162,235,0.2)',
          fill: false,
        },
        {
          label: 'VINTAGE Units',
          data: finalData.map((item) => item.vintageUnits),
          borderColor: '#FF6384',
          backgroundColor: 'rgba(255,99,132,0.2)',
          fill: false,
        },
        {
          label: 'All Units',
          data: finalData.map((item) => item.allUnits),
          borderColor: '#FFCE56',
          backgroundColor: 'rgba(255,205,86,0.2)',
          fill: false,
          hidden: !showCombined,
        },
      ],
    }
  }, [finalData, showCombined])

  const revenueData = useMemo(() => {
    return {
      labels: finalData.map((item) => item.date),
      datasets: [
        {
          label: 'EC AMZ Revenue',
          data: finalData.map((item) => item.ecRevenue),
          borderColor: '#36A2EB',
          backgroundColor: 'rgba(54,162,235,0.2)',
          fill: false,
        },
        {
          label: 'VINTAGE Revenue',
          data: finalData.map((item) => item.vintageRevenue),
          borderColor: '#FF6384',
          backgroundColor: 'rgba(255,99,132,0.2)',
          fill: false,
        },
        {
          label: 'All Revenue',
          data: finalData.map((item) => item.allRevenue),
          borderColor: '#FFCE56',
          backgroundColor: 'rgba(255,205,86,0.2)',
          fill: false,
          hidden: !showCombined,
        },
      ],
    }
  }, [finalData, showCombined])

  const options = useMemo(() => {
    return {
      plugins: {
        legend: {
          labels: {
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const val = ctx.parsed.y
              const numericVal = Number(val)
              if (activeTab === 'revenue') {
                return `${ctx.dataset.label}: $${formatNumber(numericVal)}`
              }
              return `${ctx.dataset.label}: ${formatNumber(numericVal)}`
            },
          },
        },
      },
      scales: {
        x: {
          type: 'category' as const,
          ticks: {
          },
          grid: {
            
          },
        },
        y: {
          type: logScale ? ('logarithmic' as const) : ('linear' as const),
          ticks: {
            callback: (value: any) => {
              const num = Number(value)
              if (activeTab === 'revenue') {
                return '$' + formatNumber(num)
              }
              return formatNumber(num)
            },
          },
          grid: {
            
          },
        },
      },
      responsive: true as const,
      maintainAspectRatio: false,
    }
  }, [logScale, activeTab])

  return (
    <div
      style={{
        borderRadius: '12px',
        border: 'none',
        position: 'relative',
      }}
      className='card p-4 card-body text-body bg-body'
    >
      <h2 style={{ fontWeight: 500}} className='mb-3'>
        Sales Data
      </h2>
      {loadingInitial && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            fontSize: '1.2rem',
            fontWeight: 500,
            borderRadius: '8px',
          }}
        >
          Loading chart...
        </div>
      )}
      {!loadingInitial && (
        <>
          <div className='mb-3 d-flex flex-row' style={{gap: '1rem', maxWidth: '600px'}}>
            <div style={{flex: '1'}}>
              <label style={{}} className='form-label'>
                Date Range
              </label>
              <select
                className='form-select'
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{borderRadius: '8px'}}
              >
                {dateOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {dateLabels[opt]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{flex: '1'}}>
              <label style={{}} className='form-label'>
                Interval
              </label>
              <select
                className='form-select'
                value={selectedInterval}
                onChange={(e) => setSelectedInterval(e.target.value)}
                style={{borderRadius: '8px'}}
              >
                {intervalOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ul className='nav pt-4 pb-6' style={{borderBottom: '1px solid #555'}}>
            <li className='nav-item me-4'>
              <button
                className={`btn button btn-outline ${activeTab === "units" ? "btn-primary" : ""}`}
                style={{
                  padding: '0.5rem 0.8rem',
                }}
                onClick={() => setActiveTab('units')}
              >
                Units
              </button>
            </li>
            <li className='nav-item ml-2'>
              <button
                className={`btn button btn-outline ${activeTab === "revenue" ? "btn-primary" : ""}`}
                style={{
                  padding: '0.5rem 0.8rem',
                }}
                onClick={() => setActiveTab('revenue')}
              >
                Revenue
              </button>
            </li>
          </ul>
          <div
            className='d-flex align-items-center mt-4 mb-3 pb-2 pt-2'
            style={{gap: '1rem', flexWrap: 'wrap'}}
          >
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={showCombined}
                onChange={(e) => setShowCombined(e.target.checked)}
              />
              <label className='form--label'>Show Combined</label>
            </div>
            <div className='form-check form-switch'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={logScale}
                onChange={(e) => setLogScale(e.target.checked)}
              />
              <label className='form-label'>Logarithmic Scale</label>
            </div>
            <div className='form-check form-switch'>
            {loadingPrefetch && <span>Preloading Data...</span>}
            </div>
          </div>
          <div style={{height: '520px', position: 'relative'}}>
            {activeTab === 'units' && <Line data={unitsData} options={options} />}
            {activeTab === 'revenue' && <Line data={revenueData} options={options} />}
          </div>
        </>
      )}
    </div>
  )
}
